<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  formatTokens,
  formatCost,
  calculateTotalCost,
} from '../utils/costCalculator';

const props = defineProps({
  currentMonth: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const totalTokens = computed(() => props.currentMonth?.total_tokens || 0);
const totalRequests = computed(() => props.currentMonth?.request_count || 0);
const byModel = computed(() => props.currentMonth?.by_model || {});

const estimatedCost = computed(() => {
  const cost = calculateTotalCost(byModel.value);
  // Fallback to flat rate if no by_model data
  if (cost === 0 && totalTokens.value > 0) {
    return (totalTokens.value / 1_000_000) * 2.0; // conservative fallback
  }
  return cost;
});

const avgTokensPerDay = computed(() => {
  const today = new Date().getDate();
  if (today === 0) return 0;
  return Math.round(totalTokens.value / today);
});

const cards = computed(() => [
  {
    icon: 'i-lucide-activity',
    label: t('CAPTAIN_SETTINGS.USAGE.KPI.TOTAL_TOKENS'),
    value: formatTokens(totalTokens.value),
    color: 'text-n-blue-11 bg-n-blue-3',
  },
  {
    icon: 'i-lucide-dollar-sign',
    label: t('CAPTAIN_SETTINGS.USAGE.KPI.ESTIMATED_COST'),
    value: `~${formatCost(estimatedCost.value)}`,
    color: 'text-n-teal-11 bg-n-teal-3',
  },
  {
    icon: 'i-lucide-zap',
    label: t('CAPTAIN_SETTINGS.USAGE.KPI.TOTAL_REQUESTS'),
    value: totalRequests.value.toLocaleString(),
    color: 'text-n-violet-11 bg-n-violet-3',
  },
  {
    icon: 'i-lucide-trending-up',
    label: t('CAPTAIN_SETTINGS.USAGE.KPI.AVG_TOKENS_DAY'),
    value: formatTokens(avgTokensPerDay.value),
    color: 'text-n-amber-11 bg-n-amber-3',
  },
]);
</script>

<template>
  <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
    <div
      v-for="card in cards"
      :key="card.label"
      class="flex flex-col gap-2 p-4 rounded-xl border border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-2">
        <span
          class="inline-flex items-center justify-center size-7 rounded-lg"
          :class="card.color"
        >
          <Icon :icon="card.icon" class="size-4" />
        </span>
      </div>
      <div>
        <p class="text-lg font-semibold text-n-slate-12 mb-0">
          {{ card.value }}
        </p>
        <p class="text-xs text-n-slate-11 mb-0">
          {{ card.label }}
        </p>
      </div>
    </div>
  </div>
</template>
